/* eslint-disable no-prototype-builtins */
import {ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {ILayout, useLayout} from '../../core'
import {
  ToolbarAccounting,
  ToolbarClassic,
  ToolbarExtended,
  ToolbarReports,
  ToolbarSaas,
} from './toolbars'

export interface ButtonState {
  enabled: boolean;
  className?: string;
  text?: string; // Make optional since component might not need text
  icon?: string; // Make optional since component might not need icon
  action?: () => Promise<void>; // Make optional since component handles its own actions
  primary?: boolean;
  shortcutTip?: string;
  shortcutKeys?: string[];
  component?: ReactNode; // Add component property
}

// Define the type for the toolbar state
interface ToolbarState {
  title: string;
  primaryButton: ButtonState;
  showAIChat:boolean;
  secondaryButton: ButtonState;
  tertiaryButton: ButtonState;
  alwaysDisplay:boolean;
  showBranchPicker:boolean;
  showRightToolbar:boolean;
}

// Define the context type
interface ToolbarContextType {
  toolbarState: ToolbarState;
  updateToolbar: (newState: Partial<ToolbarState>) => void;
}

const defaultButtonState: ButtonState = {
  enabled: false,
  text: '',
  icon: '',
  action: async () => {},
  primary: false,
  component: undefined, // Add default for component
};
export const defaultToolbarState: ToolbarState = {
  title: '',
  showAIChat: true,
  primaryButton: { ...defaultButtonState, primary: true },
  secondaryButton: defaultButtonState,
  tertiaryButton: defaultButtonState,
  alwaysDisplay: false,
  showBranchPicker:false,
  showRightToolbar:false,
};




export const ToolbarContext = createContext<ToolbarContextType>({
  toolbarState: defaultToolbarState,
  updateToolbar: () => {}
});

// Define the props for the provider component
interface ToolbarProviderProps {
  children: ReactNode;
}

// Create the provider component
export const ToolbarProvider: React.FC<ToolbarProviderProps> = ({ children }) => {
  const [toolbarState, setToolbarState] = useState<ToolbarState>(defaultToolbarState);

  const updateToolbar = useCallback((newState: Partial<ToolbarState>) => {
    setToolbarState((prevState) => ({
      ...prevState,
      ...newState,
      primaryButton: {
        ...prevState.primaryButton,
        ...newState.primaryButton
      },
      secondaryButton: {
        ...prevState.secondaryButton,
        ...newState.secondaryButton
      },
      tertiaryButton: {
        ...prevState.tertiaryButton,
        ...newState.tertiaryButton
      }
    }));
  }, []);

  const contextValue = useMemo(() => ({ toolbarState, updateToolbar }), [toolbarState, updateToolbar]);

  return (
    <ToolbarContext.Provider value={contextValue}>
      {children}
    </ToolbarContext.Provider>
  );
};

export const useToolbar = (): ToolbarContextType => {
  const context = useContext(ToolbarContext);
  if (!context) {
    throw new Error('useToolbar must be used within a ToolbarProvider');
  }
  return context;
};

const Toolbar = () => {
  const {config} = useLayout()
  useEffect(() => {
    updateDOM(config)
    document.body.setAttribute('data-kt-app-toolbar-enabled', 'true')
  }, [config])

  switch (config.app?.toolbar?.layout) {
    case 'classic':
      return <ToolbarClassic />
    case 'accounting':
      return <ToolbarAccounting />
    case 'extended':
      return <ToolbarExtended />
    case 'reports':
      return <ToolbarReports />
    case 'saas':
      return <ToolbarSaas />
    default:
      return <ToolbarClassic />
  }
}

const updateDOM = (config: ILayout) => {
  let appToolbarSwapAttributes: {[attrName: string]: string} = {}
  const appToolbarSwapEnabled = config.app?.toolbar?.swap?.enabled
  if (appToolbarSwapEnabled) {
    appToolbarSwapAttributes = config.app?.toolbar?.swap?.attributes as {[attrName: string]: string}
  }

  let appToolbarStickyAttributes: {[attrName: string]: string} = {}
  const appToolbarStickyEnabled = config.app?.toolbar?.sticky?.enabled
  if (appToolbarStickyEnabled) {
    appToolbarStickyAttributes = config.app?.toolbar?.sticky?.attributes as {
      [attrName: string]: string
    }

    let appToolbarMinimizeAttributes: {[attrName: string]: string} = {}
    const appToolbarMinimizeEnabled = config.app?.toolbar?.minimize?.enabled
    if (appToolbarMinimizeEnabled) {
      appToolbarMinimizeAttributes = config.app?.toolbar?.minimize?.attributes as {
        [attrName: string]: string
      }
    }

    if (config.app?.toolbar?.fixed?.desktop) {
      document.body.setAttribute('data-kt-app-toolbar-fixed', 'true')
    }

    if (config.app?.toolbar?.fixed?.mobile) {
      document.body.setAttribute('data-kt-app-toolbar-fixed-mobile', 'true')
    }

    setTimeout(() => {
      const toolbarElement = document.getElementById('kt_app_toolbar')
      // toolbar
      if (toolbarElement) {
        const toolbarAttributes = toolbarElement
          .getAttributeNames()
          .filter((t) => t.indexOf('data-') > -1)
        toolbarAttributes.forEach((attr) => toolbarElement.removeAttribute(attr))

        if (appToolbarSwapEnabled) {
          for (const key in appToolbarSwapAttributes) {
            if (appToolbarSwapAttributes.hasOwnProperty(key)) {
              toolbarElement.setAttribute(key, appToolbarSwapAttributes[key])
            }
          }
        }

        if (appToolbarStickyEnabled) {
          for (const key in appToolbarStickyAttributes) {
            if (appToolbarStickyAttributes.hasOwnProperty(key)) {
              toolbarElement.setAttribute(key, appToolbarStickyAttributes[key])
            }
          }
        }

        if (appToolbarMinimizeEnabled) {
          for (const key in appToolbarMinimizeAttributes) {
            if (appToolbarMinimizeAttributes.hasOwnProperty(key)) {
              toolbarElement.setAttribute(key, appToolbarMinimizeAttributes[key])
            }
          }
        }
      }
    }, 0)
  }
}

export {Toolbar}
